import React, { useState, useEffect, useCallback } from 'react';
import Card from './Card';
import ChallengeModal from './ChallengeModal';
import QuickPlayModal from './QuickPlayModal';
import InfoModal from './InfoModal';
import { challengesData } from '../data/challenges';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';
import { FiGrid, FiArrowLeft, FiClock, FiAward, FiInfo, FiZap } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const MILESTONE_TITLES = {
  0: "New Explorer",
  10: "Conversation Starter",
  25: "Connection Builder",
  50: "Community Weaver",
  100: "Connection Champion"
};

const GameBoard = ({ mode, onExit }) => {
  const [board, setBoard] = useState([]);
  const [completedData, setCompletedData] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [bingos, setBingos] = useState(0);
  const [showQuickPlay, setShowQuickPlay] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [pendingCardSelection, setPendingCardSelection] = useState(null);
  const [player1Confirmed, setPlayer1Confirmed] = useState(false);
  const [player2Confirmed, setPlayer2Confirmed] = useState(false);
  
  const [totalSparks, setTotalSparks] = useState(() => {
    const saved = localStorage.getItem('connectgo_total_sparks');
    return saved ? parseInt(saved, 10) : 0;
  });

  const [currentTitle, setCurrentTitle] = useState(() => {
    const saved = localStorage.getItem('connectgo_total_sparks');
    const sparks = saved ? parseInt(saved, 10) : 0;
    return getMilestoneTitle(sparks);
  });

  const [showMilestoneUnlock, setShowMilestoneUnlock] = useState(false);
  const [unlockedTitle, setUnlockedTitle] = useState('');

  function getMilestoneTitle(sparks) {
    if (sparks >= 100) return MILESTONE_TITLES[100];
    if (sparks >= 50) return MILESTONE_TITLES[50];
    if (sparks >= 25) return MILESTONE_TITLES[25];
    if (sparks >= 10) return MILESTONE_TITLES[10];
    return MILESTONE_TITLES[0];
  }

  function getNextMilestone(sparks) {
    if (sparks < 10) return { target: 10, title: MILESTONE_TITLES[10] };
    if (sparks < 25) return { target: 25, title: MILESTONE_TITLES[25] };
    if (sparks < 50) return { target: 50, title: MILESTONE_TITLES[50] };
    if (sparks < 100) return { target: 100, title: MILESTONE_TITLES[100] };
    return null;
  }

  const shuffleBoard = useCallback(() => {
    const shuffled = [...challengesData].sort(() => Math.random() - 0.5);
    setBoard(shuffled);
    setCompletedData([]);
    setBingos(0);
  }, []);

  useEffect(() => {
    shuffleBoard();
  }, [shuffleBoard]);

  useEffect(() => {
    if (board.length === 0) return;
    const checkConnections = () => {
      let count = 0;
      const size = 4;
      const completedIds = completedData.map(d => d.id);

      for (let i = 0; i < size; i++) {
        const row = board.slice(i * size, (i + 1) * size);
        if (row.every(c => completedIds.includes(c.id))) count++;
      }

      for (let i = 0; i < size; i++) {
        const col = [board[i], board[i + size], board[i + size * 2], board[i + size * 3]];
        if (col.every(c => completedIds.includes(c.id))) count++;
      }

      const diag1 = [board[0], board[5], board[10], board[15]];
      if (diag1.every(c => completedIds.includes(c.id))) count++;

      const diag2 = [board[3], board[6], board[9], board[12]];
      if (diag2.every(c => completedIds.includes(c.id))) count++;

      if (count > bingos) triggerBigCelebration();
      setBingos(count);
    };
    checkConnections();
  }, [completedData, board, bingos]);

  const toggleComplete = (id) => {
    if (!completedData.find(d => d.id === id)) {
      const newCompletedData = [...completedData, { id, player: currentPlayer }];
      setCompletedData(newCompletedData);

      const newTotal = totalSparks + 1;
      setTotalSparks(newTotal);
      localStorage.setItem('connectgo_total_sparks', newTotal.toString());

      const newTitle = getMilestoneTitle(newTotal);
      if (newTitle !== currentTitle) {
        setCurrentTitle(newTitle);
        setUnlockedTitle(newTitle);
        setShowMilestoneUnlock(true);
        setTimeout(() => setShowMilestoneUnlock(false), 4000);
      }

      triggerConfetti();
      if (mode === 'duo') {
        setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
      }
    }
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 50,
      spread: 60,
      origin: { y: 0.8 },
      colors: ['#6366f1', '#2563eb', '#22d3ee']
    });
  };

  const triggerBigCelebration = () => {
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.6 },
      colors: ['#6366f1', '#22d3ee', '#fbbf24']
    });
  };

  const handleCardClick = (challenge) => {
    if (mode === 'duo') {
      setPendingCardSelection(challenge);
      setPlayer1Confirmed(false);
      setPlayer2Confirmed(false);
    } else {
      setSelectedChallenge(challenge);
    }
  };

  const handlePlayerConfirm = (playerNum) => {
    if (playerNum === 1) {
      setPlayer1Confirmed(true);
    } else {
      setPlayer2Confirmed(true);
    }
  };

  const handleCancelSelection = () => {
    setPendingCardSelection(null);
    setPlayer1Confirmed(false);
    setPlayer2Confirmed(false);
  };

  useEffect(() => {
    if (player1Confirmed && player2Confirmed && pendingCardSelection) {
      setSelectedChallenge(pendingCardSelection);
      setPendingCardSelection(null);
      setPlayer1Confirmed(false);
      setPlayer2Confirmed(false);
    }
  }, [player1Confirmed, player2Confirmed, pendingCardSelection]);

  const progress = Math.round((completedData.length / 16) * 100);
  const nextMilestone = getNextMilestone(totalSparks);

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-6">
      <div className="bg-white rounded-[2rem] shadow-lg border border-slate-200 p-5 md:p-6 mb-8 sticky top-4 z-40 backdrop-blur-md bg-white/95">
        <div className="flex flex-col md:flex-row gap-5 items-center justify-between">
          <div className="flex items-center justify-between w-full md:w-auto gap-4">
            <button 
              onClick={onExit}
              className="p-2.5 text-slate-400 hover:text-slate-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
            </button>
            
            <div className="flex items-baseline whitespace-nowrap">
              <span className="text-xl font-black text-slate-900 tracking-tighter">Connect</span>
              <span className="text-xl font-black italic text-transparent bg-clip-text bg-gradient-to-br from-indigo-600 to-cyan-500 tracking-tighter pr-2">Go</span>
            </div>

            <button 
              onClick={() => setShowInfo(true)}
              className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"
              title="How it works"
            >
              <SafeIcon icon={FiInfo} className="w-5 h-5" />
            </button>

            {mode === 'duo' && (
              <div className="flex items-center gap-2 bg-indigo-50 px-3 py-1.5 rounded-full border border-indigo-100">
                <div className={`w-2 h-2 rounded-full animate-pulse ${currentPlayer === 1 ? 'bg-indigo-500' : 'bg-rose-500'}`} />
                <span className="text-[10px] font-black uppercase text-indigo-900">Player {currentPlayer}'s Turn</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 w-full md:max-w-sm">
            <div className="bg-slate-50/50 p-2 rounded-2xl text-center">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Sparks</span>
              <div className="flex items-center justify-center gap-1.5">
                <SafeIcon icon={FiZap} className="text-amber-500 w-4 h-4 fill-amber-500" />
                <span className="text-lg font-black text-slate-900">{completedData.length}</span>
              </div>
            </div>

            <div className="bg-slate-50/50 p-2 rounded-2xl text-center border-x border-slate-100">
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1">Grid</span>
              <div className="flex items-center justify-center gap-1.5">
                <SafeIcon icon={FiGrid} className="text-indigo-600 w-4 h-4" />
                <span className="text-lg font-black text-slate-900">{bingos}</span>
              </div>
            </div>

            <div className="bg-slate-50/50 p-2 rounded-2xl flex flex-col justify-center px-3">
              <div className="flex justify-between text-[9px] font-black text-slate-400 uppercase mb-1.5">
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <motion.div 
                  animate={{ width: `${progress}%` }}
                  className="h-full bg-slate-900"
                />
              </div>
            </div>
          </div>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 space-y-2"
        >
          <div className="flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-50 to-cyan-50 px-4 py-2 rounded-xl border border-indigo-100">
            <SafeIcon icon={FiAward} className="text-indigo-600 w-4 h-4" />
            <span className="text-xs font-black text-indigo-900">{currentTitle}</span>
            <span className="text-[10px] text-slate-500 font-medium">• {totalSparks} Total Sparks</span>
          </div>

          {nextMilestone && (
            <div className="bg-slate-50 px-4 py-2 rounded-xl border border-slate-100">
              <div className="flex items-center justify-between text-[10px] mb-1.5">
                <span className="font-medium text-slate-500">
                  Next: {nextMilestone.title}
                </span>
                <span className="font-black text-slate-900">
                  {totalSparks}/{nextMilestone.target}
                </span>
              </div>
              <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                <motion.div 
                  animate={{ width: `${(totalSparks / nextMilestone.target) * 100}%` }}
                  className="h-full bg-gradient-to-r from-indigo-500 to-cyan-500"
                />
              </div>
            </div>
          )}
        </motion.div>
      </div>

      <div className="mb-6 flex justify-center">
        <button 
          onClick={() => setShowQuickPlay(true)}
          className="group bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-2xl font-black text-sm shadow-lg hover:shadow-xl transition-all active:scale-95 flex items-center gap-2"
        >
          <SafeIcon icon={FiClock} className="w-4 h-4" />
          Quick Play (5 min)
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {board.map((challenge, index) => {
          const completion = completedData.find(d => d.id === challenge.id);
          return (
            <Card 
              key={`${challenge.id}-${index}`}
              challenge={challenge}
              isCompleted={!!completion}
              player={completion?.player}
              onClick={() => handleCardClick(challenge)}
            />
          );
        })}
      </div>

      <AnimatePresence>
        {pendingCardSelection && mode === 'duo' && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
            onClick={handleCancelSelection}
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center space-y-6">
                <div className={`w-16 h-16 mx-auto rounded-2xl ${pendingCardSelection.gradient} text-white flex items-center justify-center shadow-lg`}>
                  <SafeIcon icon={pendingCardSelection.icon} className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2">
                    {pendingCardSelection.title}
                  </h3>
                  <p className="text-sm text-slate-500 font-medium">
                    Both players must agree to explore this challenge
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePlayerConfirm(1)}
                    className={`p-4 rounded-xl font-black text-sm transition-all ${player1Confirmed ? 'bg-indigo-500 text-white shadow-lg' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                  >
                    {player1Confirmed ? '✓ Player 1' : 'Player 1'}
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handlePlayerConfirm(2)}
                    className={`p-4 rounded-xl font-black text-sm transition-all ${player2Confirmed ? 'bg-rose-500 text-white shadow-lg' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                  >
                    {player2Confirmed ? '✓ Player 2' : 'Player 2'}
                  </motion.button>
                </div>

                <button 
                  onClick={handleCancelSelection}
                  className="text-sm text-slate-400 hover:text-slate-600 font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <InfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />
      
      <ChallengeModal 
        challenge={selectedChallenge}
        isOpen={!!selectedChallenge}
        onClose={() => setSelectedChallenge(null)}
        onComplete={toggleComplete}
        isCompleted={selectedChallenge && !!completedData.find(d => d.id === selectedChallenge.id)}
        mode={mode}
        currentPlayer={currentPlayer}
      />

      <QuickPlayModal 
        isOpen={showQuickPlay}
        onClose={() => setShowQuickPlay(false)}
        onComplete={toggleComplete}
        mode={mode}
      />

      <AnimatePresence>
        {showMilestoneUnlock && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] bg-gradient-to-r from-indigo-600 to-cyan-500 text-white px-8 py-4 rounded-2xl shadow-2xl"
          >
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiAward} className="w-6 h-6 animate-bounce" />
              <div>
                <p className="text-xs font-medium opacity-90">New Title Unlocked!</p>
                <p className="text-lg font-black">{unlockedTitle}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameBoard;